import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  IconButton,
  Grid,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon, Edit as EditIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AddPayrollStructureDialog from "components/AddPayrollStructureDialog";
import AddStructureComponentDialog from "components/AddStructureComponentDialog";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import LoadingScreen from "components/Loading";
import {
  getPayrollStructures,
  addPayrollStructure,
  updatePayrollStructure,
  deletePayrollStructure,
  getComponentsForStructure,
  addStructureComponent,
  deleteStructureComponent,
  getActiveSalaryComponents,
  getJobs,
} from "services/api";
import MDButton from "components/MDButton";

const StructuresView = ({ canEdit }) => {
  const { t } = useTranslation();
  const snackbarRef = useRef();

  const [loading, setLoading] = useState(true);
  const [structures, setStructures] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);

  const [selectedStructure, setSelectedStructure] = useState(null);
  const [structureComponents, setStructureComponents] = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(false);

  const [structureDialogOpen, setStructureDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [structureBeingEdited, setStructureBeingEdited] = useState(null);

  const [componentDialogOpen, setComponentDialogOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [structureToDelete, setStructureToDelete] = useState(null);

  // Initial load: structures, jobs (for the dropdown), active components (for attaching)
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      const [structuresRes, jobsRes, componentsRes] = await Promise.all([
        getPayrollStructures(),
        getJobs(),
        getActiveSalaryComponents(),
      ]);
      if (structuresRes !== "error") setStructures(structuresRes);
      if (jobsRes !== "error") setJobs(jobsRes);
      if (componentsRes !== "error") setAvailableComponents(componentsRes);
      setLoading(false);
    };
    loadAll();
  }, []);

  const loadComponentsForStructure = async (structureId) => {
    setComponentsLoading(true);
    const res = await getComponentsForStructure(structureId);
    if (res !== "error") {
      setStructureComponents(res);
    } else {
      snackbarRef.current?.showSnackbar(t("admin.payroll.structures.loadComponentsError"), "error");
    }
    setComponentsLoading(false);
  };

  const handleSelectStructure = (structure) => {
    setSelectedStructure(structure);
    loadComponentsForStructure(structure.id);
  };

  const handleOpenAdd = () => {
    setStructureBeingEdited(null);
    setEditMode(false);
    setStructureDialogOpen(true);
  };

  const handleOpenEdit = (structure, event) => {
    event.stopPropagation();
    setStructureBeingEdited(structure);
    setEditMode(true);
    setStructureDialogOpen(true);
  };

  const handleSubmitStructure = async (form) => {
    if (editMode) {
      const res = await updatePayrollStructure(structureBeingEdited.id, form);
      if (res !== "error") {
        setStructures(structures.map((s) => (s.id === res.id ? res : s)));
        if (selectedStructure?.id === res.id) setSelectedStructure(res);
        snackbarRef.current?.showSnackbar(t("admin.payroll.structures.updateSuccess"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("admin.payroll.structures.updateError"), "error");
      }
    } else {
      const res = await addPayrollStructure(form);
      if (res !== "error") {
        setStructures([...structures, res]);
        snackbarRef.current?.showSnackbar(t("admin.payroll.structures.addSuccess"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("admin.payroll.structures.addError"), "error");
      }
    }
    setStructureDialogOpen(false);
  };

  const handleDeleteClick = () => {
    setStructureDialogOpen(false);
    setStructureToDelete(structureBeingEdited);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const res = await deletePayrollStructure(structureToDelete.id);
    if (res === "success") {
      setStructures(structures.filter((s) => s.id !== structureToDelete.id));
      if (selectedStructure?.id === structureToDelete.id) {
        setSelectedStructure(null);
        setStructureComponents([]);
      }
      snackbarRef.current?.showSnackbar(t("admin.payroll.structures.deleteSuccess"), "success");
    } else if (typeof res === "string" && res.startsWith("Cannot delete")) {
      snackbarRef.current?.showSnackbar(t("admin.payroll.structures.cannotDelete"), "error");
    } else {
      snackbarRef.current?.showSnackbar(t("admin.payroll.structures.deleteError"), "error");
    }
    setConfirmOpen(false);
  };

  const handleAddComponent = async (payload) => {
    const res = await addStructureComponent({
      ...payload,
      payrollStructureId: selectedStructure.id,
    });
    if (res !== "error" && typeof res !== "string") {
      setStructureComponents([...structureComponents, res]);
      snackbarRef.current?.showSnackbar(t("admin.payroll.structures.componentAdded"), "success");
    } else {
      // Backend rejects duplicates or amount/percentage mismatches with a clear message
      snackbarRef.current?.showSnackbar(
        typeof res === "string" ? res : t("admin.payroll.structures.componentAddError"),
        "error"
      );
    }
  };

  const handleRemoveComponent = async (componentRowId) => {
    const res = await deleteStructureComponent(componentRowId);
    if (res === "success") {
      setStructureComponents(structureComponents.filter((c) => c.id !== componentRowId));
      snackbarRef.current?.showSnackbar(t("admin.payroll.structures.componentRemoved"), "success");
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.structures.componentRemoveError"),
        "error"
      );
    }
  };

  const componentName = (salaryComponentId) => {
    const match = availableComponents.find((c) => c.id === salaryComponentId);
    return match ? match.name : `#${salaryComponentId}`;
  };

  const jobName = (jobId) => {
    if (!jobId) return t("admin.payroll.structures.noJobLink");
    const match = jobs.find((j) => j.jobId === jobId);
    return match ? match.jobName : `#${jobId}`;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left panel - structures list */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <MDBox
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <MDTypography variant="subtitle2" color="text">
                {t("admin.payroll.structures.title")}
              </MDTypography>
              {canEdit && (
                <MDButton
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAdd}
                >
                  {t("admin.payroll.structures.addButton")}
                </MDButton>
              )}
            </MDBox>

            <List dense>
              {structures.map((structure) => (
                <ListItemButton
                  key={structure.id}
                  selected={selectedStructure?.id === structure.id}
                  onClick={() => handleSelectStructure(structure)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={structure.name}
                    secondary={`${jobName(structure.jobId)} · ${structure.currency}`}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                  {canEdit && (
                    <IconButton size="small" onClick={(e) => handleOpenEdit(structure, e)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Right panel - components for the selected structure */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {selectedStructure ? (
              <>
                <MDBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <MDBox>
                    <MDTypography variant="h6">{selectedStructure.name}</MDTypography>
                    <MDTypography variant="caption" color="text">
                      {selectedStructure.currency} · {selectedStructure.countryCode}
                    </MDTypography>
                  </MDBox>
                  {canEdit && (
                    <MDButton
                      color="primary"
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setComponentDialogOpen(true)}
                    >
                      {t("admin.payroll.structures.addComponent")}
                    </MDButton>
                  )}
                </MDBox>

                {componentsLoading ? (
                  <LoadingScreen />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.payroll.structures.component")}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            {t("admin.payroll.structures.amount")}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {t("admin.payroll.structures.mandatory")}
                          </TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                        {structureComponents.map((comp) => (
                          <TableRow key={comp.id}>
                            <TableCell>{componentName(comp.salaryComponentId)}</TableCell>
                            <TableCell align="right">
                              {comp.percentage != null
                                ? `${comp.percentage}%`
                                : comp.amount?.toLocaleString()}
                            </TableCell>
                            <TableCell align="center">
                              {comp.isMandatory && (
                                <Chip
                                  label={t("admin.payroll.structures.required")}
                                  color="success"
                                  size="small"
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {canEdit && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveComponent(comp.id)}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <MDBox sx={{ py: 4, textAlign: "center" }}>
                <MDTypography color="text">
                  {t("admin.payroll.structures.selectStructure")}
                </MDTypography>
              </MDBox>
            )}
          </Paper>
        </Grid>
      </Grid>

      <AddPayrollStructureDialog
        open={structureDialogOpen}
        onClose={() => setStructureDialogOpen(false)}
        onSubmit={handleSubmitStructure}
        structure={structureBeingEdited}
        editMode={editMode}
        handleDelete={handleDeleteClick}
        jobs={jobs}
      />

      <AddStructureComponentDialog
        open={componentDialogOpen}
        onClose={() => setComponentDialogOpen(false)}
        onSubmit={handleAddComponent}
        availableComponents={availableComponents.filter(
          (c) => !structureComponents.some((sc) => sc.salaryComponentId === c.id)
        )}
      />

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        message={t("admin.payroll.structures.confirmDelete", { name: structureToDelete?.name })}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

StructuresView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default StructuresView;
